import { PrimitiveType, } from "@buildwithflux/models";
import intersects from "intersects";
import { makeCCW, quickDecomp } from "poly-decomp-es";
import { Matrix3, Vector2 } from "three";
import { DecompMaxLevel, vec2 } from "../../../common";
import { pointDistance } from "./distance";
/** Checks if a 2D point is contained inside a primitive */
export function pointInShape(primitive, point) {
    switch (primitive.type) {
        case PrimitiveType.Union:
            return unionPointInShape(primitive, point);
        case PrimitiveType.Difference:
            return differencePointInShape(primitive, point);
        case PrimitiveType.LineArc:
            return PointIn.lineArc(primitive, point); // It's easier to determine containment using a non-sdf approach for this primitive type
        default:
            return pointDistance(primitive, point.x, point.y).dist <= 0;
    }
}
function unionPointInShape(primitive, point) {
    const pointPrimitiveSpace = vec2.applyMatrix3({ x: point.x, y: point.y }, primitive.transformInverse);
    return (pointInShape(primitive.primitiveA, pointPrimitiveSpace) ||
        pointInShape(primitive.primitiveB, pointPrimitiveSpace));
}
function differencePointInShape(primitive, point) {
    const pointPrimitiveSpace = vec2.applyMatrix3({ x: point.x, y: point.y }, primitive.transformInverse);
    return (pointInShape(primitive.primitiveA, pointPrimitiveSpace) &&
        !pointInShape(primitive.primitiveB, pointPrimitiveSpace));
}
// TODO: move into Query below
export const PointIn = {
    // Why do this? avoids dependence on clipper factory - pointInShape too generalized, requires every caller to have clipper factory on hand
    lineArc(primitive, point) {
        const pointPrimitiveSpace = vec2.applyMatrix3({ x: point.x, y: point.y }, primitive.transformInverse);
        const halfThickness = primitive.thickness / 2;
        const distance = vec2.distance({ x: primitive.centerX, y: primitive.centerY }, pointPrimitiveSpace);
        const radiusInner = primitive.radius - halfThickness;
        const radiusOuter = primitive.radius + halfThickness;
        if (distance < radiusInner) {
            return false;
        }
        if (distance > radiusOuter) {
            return false;
        }
        const ptVec = vec2.sub(pointPrimitiveSpace, { x: primitive.centerX, y: primitive.centerY });
        const ptFirst = {
            x: primitive.centerX + Math.cos(primitive.startAngle) * primitive.radius,
            y: primitive.centerY + Math.sin(primitive.startAngle) * primitive.radius,
        };
        const ptVecFirst = vec2.sub(ptFirst, { x: primitive.centerX, y: primitive.centerY });
        const ptTheta = vec2.radiansBetween(ptVec, ptVecFirst);
        const rangeAngle = primitive.endAngle - primitive.startAngle;
        if (vec2.dot(ptVecFirst, ptVec) < 0) {
            return false;
        }
        if (ptTheta > rangeAngle) {
            return false;
        }
        return true;
    },
    lineSegment(primitive, point) {
        const pointPrimitiveSpace = vec2.applyMatrix3({ x: point.x, y: point.y }, primitive.transformInverse);
        const hVec = vec2.sub(pointPrimitiveSpace, { x: primitive.startX, y: primitive.startY });
        const aVec = vec2.sub({ x: primitive.endX, y: primitive.endY }, { x: primitive.startX, y: primitive.startY });
        const aLength = vec2.len(aVec);
        if (aLength === 0) {
            return false;
        }
        const aNormal = vec2.scale(aVec, 1 / aLength);
        const aScalar = vec2.dot(hVec, aNormal);
        if (aScalar < 0) {
            // TODO: account for rounded caps
            return false;
        }
        if (aScalar > aLength) {
            // TODO: account for rounded caps
            return false;
        }
        const proj = vec2.add(vec2.scale(aNormal, aScalar), { x: primitive.startX, y: primitive.startY });
        const distance = vec2.distance(proj, pointPrimitiveSpace);
        if (distance > primitive.thickness / 2) {
            return false;
        }
        return true;
    },
};
/**
 * Fluent API for a range of primitive to primitive queries
 */
export const Query = {
    /**
     * BoundBox is an AABB rectangle. Note that it is assumed to exist in the same space as the primitives
     * it's compared with.
     */
    BoundBox: (min, max) => {
        const bbWidth = Math.abs(max.x - min.x);
        const bbHeight = Math.abs(max.y - min.y);
        return {
            Overlaps: {
                /**
                 * Checks if a circle overlaps with the bounds
                 */
                Circle: (radius, transform) => {
                    const matrix = new Matrix3().fromArray(transform);
                    const center = new Vector2(0, 0).applyMatrix3(matrix);
                    return intersects.boxCircle(min.x, min.y, bbWidth, bbHeight, center.x, center.y, radius);
                },
                /**
                 * Checks if a rectangle overlaps with the bounds. This works by checking if any of the
                 * rectangle overlaps circles at either end of the oblong - this is a cheap operation. If
                 * yes, we early exit. If not, we check if the rectangle overlaps the inner (OABB) rectangle
                 * of the oblong.
                 */
                Oblong: (width, height, transform) => {
                    const matrix = new Matrix3().fromArray(transform);
                    const horizontal = width > height;
                    const diameter = horizontal ? height : width;
                    const radius = diameter / 2;
                    const halfAxisMinusRadius = horizontal
                        ? new Vector2((width - diameter) / 2, 0)
                        : new Vector2(0, (height - diameter) / 2);
                    const neg = halfAxisMinusRadius.clone().negate().applyMatrix3(matrix);
                    if (intersects.boxCircle(min.x, min.y, bbWidth, bbHeight, neg.x, neg.y, radius)) {
                        return true;
                    }
                    const pos = halfAxisMinusRadius.clone().applyMatrix3(matrix);
                    if (intersects.boxCircle(min.x, min.y, bbWidth, bbHeight, pos.x, pos.y, radius)) {
                        return true;
                    }
                    const perp = new Vector2(halfAxisMinusRadius.y, -halfAxisMinusRadius.x).setLength(radius);
                    const TL = halfAxisMinusRadius.clone().add(perp).applyMatrix3(matrix);
                    const TR = halfAxisMinusRadius.clone().negate().add(perp).applyMatrix3(matrix);
                    const BL = halfAxisMinusRadius.clone().sub(perp).applyMatrix3(matrix);
                    const BR = halfAxisMinusRadius.clone().negate().sub(perp).applyMatrix3(matrix);
                    const corners = [
                        [TL.x, TL.y],
                        [TR.x, TR.y],
                        [BR.x, BR.y],
                        [BL.x, BL.y],
                    ];
                    makeCCW(corners);
                    return quickDecomp(corners, undefined, undefined, undefined, undefined, DecompMaxLevel).some((poly) => {
                        if (intersects.boxPolygon(min.x, min.y, bbWidth, bbHeight, poly.flat())) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    });
                },
            },
        };
    },
};
//# sourceMappingURL=containment.js.map